import { ErrorClass, isRecoverableError } from '../../domain/flow'
import { getErrorMessage } from '../../shared/error'

// ── Wallet UX errors ──
// These are wallet-connect-time UX problems that don't map to ErrorClass
// (ErrorClass is for the swap flow). They surface in the wallet-connect
// buttons' local uiError only, not in the shared state.lastError.
export const WalletUxError = {
  MOBILE_SOLANA_WALLET_NOT_DETECTED: 'mobile_solana_wallet_not_detected',
  SOLANA_WALLET_NOT_DETECTED: 'solana_wallet_not_detected',
  PHANTOM_WALLET_UNAVAILABLE: 'phantom_wallet_unavailable',
  SOLFLARE_WALLET_UNAVAILABLE: 'solflare_wallet_unavailable',
  MOBILE_EVM_WALLET_NOT_CONFIGURED: 'mobile_evm_wallet_not_configured',
  WALLETCONNECT_PROJECT_ID_MISSING: 'walletconnect_project_id_missing',
  INJECTED_WALLET_UNAVAILABLE: 'injected_wallet_unavailable',
  SOLANA_WALLET_CONNECT_TIMEOUT: 'solana_wallet_connect_timeout',
  SOLANA_WALLETCONNECT_PROJECT_ID_MISSING: 'solana_walletconnect_project_id_missing',
  SOLANA_WC_CONNECTION_RESET: 'solana_wc_connection_reset',
  SOLANA_WC_NAMESPACE_MISSING: 'solana_wc_namespace_missing',
  PHANTOM_DEEPLINK_SUSPENDED: 'phantom_deeplink_suspended',
} as const
export type WalletUxError = (typeof WalletUxError)[keyof typeof WalletUxError]

// ── Raw error string → ErrorClass mapping ──
// Maps the raw strings thrown from services/*.ts into the canonical ErrorClass.
// Exact-match first, then prefix/substring checks for classes where upstream
// code decorates the message (e.g. 'bridge_failed: Layerswap swap failed').

const EXACT_ERROR_MAP: Record<string, ErrorClass> = {
  wallet_not_connected: ErrorClass.WALLET_NOT_CONNECTED,
  wallet_session_dead: ErrorClass.WALLET_SESSION_DEAD,
  wallet_signature_timeout: ErrorClass.WALLET_SESSION_DEAD,
  wallet_namespace_mismatch: ErrorClass.WALLET_NAMESPACE_MISMATCH,
  unsupported_chain: ErrorClass.UNSUPPORTED_CHAIN,
  wrong_chain: ErrorClass.WRONG_CHAIN,
  route_not_found: ErrorClass.ROUTE_NOT_FOUND,
  amount_too_small: ErrorClass.AMOUNT_TOO_SMALL,
  amount_too_large: ErrorClass.AMOUNT_TOO_LARGE,
  approval_rejected: ErrorClass.APPROVAL_REJECTED,
  swap_rejected: ErrorClass.SWAP_REJECTED,
  swap_timeout: ErrorClass.SWAP_TIMEOUT,
  bridge_failed: ErrorClass.BRIDGE_FAILED,
  bridge_create_failed: ErrorClass.BRIDGE_CREATE_FAILED,
  ton_tx_failed: ErrorClass.TON_TX_FAILED,
  ton_tx_uncertain: ErrorClass.TON_TX_UNCERTAIN,
  ton_sync_required: ErrorClass.TON_SYNC_REQUIRED,
  solana_tx_failed: ErrorClass.SOLANA_TX_FAILED,
  solana_tx_uncertain: ErrorClass.SOLANA_TX_UNCERTAIN,
  solana_swap_failed: ErrorClass.SOLANA_SWAP_FAILED,
  insufficient_sol_fees: ErrorClass.INSUFFICIENT_SOL_FEES,
  quote_timeout: ErrorClass.QUOTE_TIMEOUT,
}

/**
 * Classify a raw error (Error, string, or ErrorClass value) into an ErrorClass.
 * Returns null if the error cannot be classified.
 */
export function classifyError(error: unknown): ErrorClass | null {
  if (error === null || error === undefined) return null
  const message = getErrorMessage(error)
  if (!message) return null

  if (EXACT_ERROR_MAP[message]) return EXACT_ERROR_MAP[message]

  // Prefix-decorated variants used by upstream services.
  // bridge_create_failed must be checked BEFORE bridge_failed so the more
  // specific class wins (the bridge_failed prefix would otherwise match
  // 'bridge_create_failed' as a startsWith).
  if (message.startsWith('bridge_create_failed')) return ErrorClass.BRIDGE_CREATE_FAILED
  if (message.startsWith('bridge_failed')) return ErrorClass.BRIDGE_FAILED
  if (message.startsWith('amount_too_small')) return ErrorClass.AMOUNT_TOO_SMALL
  if (message.startsWith('amount_too_large')) return ErrorClass.AMOUNT_TOO_LARGE
  if (message.startsWith('quote_stale')) return ErrorClass.ROUTE_NOT_FOUND

  // WalletConnect relay dead → treat as wallet session dead.
  if (message.includes('Failed to publish payload')) return ErrorClass.WALLET_SESSION_DEAD

  // Reown AppKit / WalletConnect throws this when our code calls
  // `provider.request(...)` on a Wallet Standard provider that doesn't
  // expose a JSON-RPC surface (Phantom/Solflare browser extensions). The
  // sign-and-send dispatcher now branches on provider shape so this
  // shouldn't fire — but classify it as a safety net so users see
  // actionable copy if the dispatcher misses a future provider variant.
  const lower = message.toLowerCase()
  if (lower.includes('does not support') && lower.includes('"request"')) {
    return ErrorClass.WALLET_SESSION_DEAD
  }

  return null
}

const ERROR_CLASS_MESSAGES: Record<ErrorClass, string> = {
  [ErrorClass.SWAP_REJECTED]: 'Swap was rejected. Please try again.',
  [ErrorClass.WRONG_CHAIN]: 'Please switch to the correct network in your wallet and try again.',
  [ErrorClass.APPROVAL_REJECTED]: 'Token approval was rejected. Please try again.',
  [ErrorClass.ROUTE_NOT_FOUND]: 'No route found for this token. Try a different token or amount.',
  [ErrorClass.AMOUNT_TOO_SMALL]:
    'Amount is too small. Try a larger amount to meet the route minimum.',
  [ErrorClass.AMOUNT_TOO_LARGE]: 'Amount is too large for this bridge route. Try a smaller amount.',
  [ErrorClass.BRIDGE_FAILED]: 'Bridge transaction failed. Please try again.',
  [ErrorClass.BRIDGE_CREATE_FAILED]:
    'Bridge step couldn’t reach the network. Your USDT is safe in your wallet — tap Retry to finish the bridge.',
  [ErrorClass.SWAP_TIMEOUT]: 'Swap timed out. Please check your wallet for pending transactions.',
  [ErrorClass.UNSUPPORTED_CHAIN]: 'This chain is not supported.',
  [ErrorClass.WALLET_NOT_CONNECTED]: 'Wallet is not connected.',
  [ErrorClass.WALLET_SESSION_DEAD]:
    'Wallet session was lost before the transaction could be delivered. Please reconnect your wallet and try again.',
  [ErrorClass.WALLET_NAMESPACE_MISMATCH]:
    'Wrong wallet for this network. Please reconnect from the correct chain.',
  [ErrorClass.TON_TX_FAILED]: 'TON transaction failed. Please try again.',
  [ErrorClass.TON_TX_UNCERTAIN]:
    'The TON wallet did not return a final transaction result. Check your wallet activity before starting another swap.',
  [ErrorClass.TON_SYNC_REQUIRED]: 'Confirming the TON transaction. No action is required.',
  [ErrorClass.SOLANA_TX_FAILED]:
    'Solana wallet could not sign or send the transaction. Please try again.',
  [ErrorClass.SOLANA_TX_UNCERTAIN]:
    'Solana confirmation is still unresolved. Do not retry yet; the transaction may still land.',
  [ErrorClass.SOLANA_SWAP_FAILED]: 'Jupiter route failed on-chain. Try again with a fresh quote.',
  [ErrorClass.INSUFFICIENT_SOL_FEES]:
    'Not enough SOL for transaction fees. Top up at least 0.005 SOL on Solana and try again.',
  [ErrorClass.QUOTE_TIMEOUT]: 'Quote request timed out. Please try again.',
  [ErrorClass.ROUTE_REVERTED]:
    "This route can't be executed right now. Try a smaller amount or a different token.",
}

const WALLET_UX_MESSAGES: Record<WalletUxError, string> = {
  [WalletUxError.MOBILE_SOLANA_WALLET_NOT_DETECTED]:
    'No Solana wallet detected. Tap Connect to pair via WalletConnect (QR / deep-link).',
  [WalletUxError.SOLANA_WALLET_NOT_DETECTED]:
    'No Solana wallet detected. Please install Phantom or Solflare browser extension.',
  [WalletUxError.PHANTOM_WALLET_UNAVAILABLE]:
    'Phantom wallet not found. Please install the Phantom extension.',
  [WalletUxError.SOLFLARE_WALLET_UNAVAILABLE]:
    'Solflare wallet not found. Please install the Solflare extension.',
  [WalletUxError.MOBILE_EVM_WALLET_NOT_CONFIGURED]:
    'Mobile wallet connection is not available. Open this page in the MetaMask or Trust Wallet app\u2019s built-in browser.',
  [WalletUxError.WALLETCONNECT_PROJECT_ID_MISSING]:
    'WalletConnect is not configured. Please use a browser with a wallet extension.',
  [WalletUxError.INJECTED_WALLET_UNAVAILABLE]:
    'No EVM wallet detected. Please install MetaMask or open this page in your wallet\u2019s built-in browser.',
  [WalletUxError.SOLANA_WALLET_CONNECT_TIMEOUT]:
    'Wallet connection timed out. Please try again or restart your wallet app.',
  [WalletUxError.SOLANA_WALLETCONNECT_PROJECT_ID_MISSING]:
    'WalletConnect is not configured. Ask the site owner to set VITE_WALLETCONNECT_PROJECT_ID.',
  [WalletUxError.SOLANA_WC_CONNECTION_RESET]: 'Connection cancelled. Tap Connect again.',
  [WalletUxError.SOLANA_WC_NAMESPACE_MISSING]:
    'Wallet did not approve Solana access. Try Connect again and approve Solana when asked.',
  [WalletUxError.PHANTOM_DEEPLINK_SUSPENDED]:
    'Phantom didn\u2019t open. Make sure the Phantom app is installed and try again.',
}

const PHANTOM_COORDINATION_MESSAGES: Readonly<Record<string, string>> = {
  phantom_attempt_not_claimed:
    'Another Phantom request is already in progress. Return to it or cancel the current swap before trying again.',
  phantom_coordination_unavailable:
    'Phantom request coordination is unavailable in this browser. Close other tabs and try again.',
}

/**
 * Get the user-facing message for a given ErrorClass.
 * Returns null for null input so callers can branch on presence.
 */
export function getUserMessage(errorClass: ErrorClass | null): string | null {
  if (!errorClass) return null
  return ERROR_CLASS_MESSAGES[errorClass]
}

/**
 * Get the user-facing message for a raw error — resolves ErrorClass first,
 * then falls back to WalletUxError table, then to the raw error message.
 * Used by wallet-connect button handlers that receive arbitrary errors.
 */
export function getUserMessageForRawError(error: unknown): string {
  const errorClass = classifyError(error)
  if (errorClass) return ERROR_CLASS_MESSAGES[errorClass]

  const message = getErrorMessage(error)
  if (message in WALLET_UX_MESSAGES) {
    return WALLET_UX_MESSAGES[message as WalletUxError]
  }
  if (message in PHANTOM_COORDINATION_MESSAGES) {
    return PHANTOM_COORDINATION_MESSAGES[message] ?? 'Phantom request could not be coordinated.'
  }
  return message
}

const RECOVERY_HINTS: Partial<Record<ErrorClass, string>> = {
  [ErrorClass.AMOUNT_TOO_SMALL]:
    'Try the suggested amount or increase the amount to meet the route minimum.',
  [ErrorClass.AMOUNT_TOO_LARGE]:
    'Lower the amount to fit the bridge route cap, or split it across two swaps.',
  [ErrorClass.ROUTE_NOT_FOUND]:
    'Try another token on this network or switch to a network with native liquidity.',
  [ErrorClass.UNSUPPORTED_CHAIN]: 'Switch to Ethereum, Arbitrum, Base, Optimism, or Solana.',
  [ErrorClass.WALLET_NOT_CONNECTED]: 'Reconnect your TON wallet and try again.',
  [ErrorClass.WALLET_SESSION_DEAD]:
    'Reconnect the wallet for this route, then retry the transaction.',
  [ErrorClass.WALLET_NAMESPACE_MISMATCH]:
    'Disconnect and reconnect to switch wallet/network. Make sure you tap your EVM wallet (MetaMask / Trust Wallet), not Phantom.',
  [ErrorClass.INSUFFICIENT_SOL_FEES]:
    'Send a small amount of SOL (≥0.005) to your wallet on Solana, then retry.',
  [ErrorClass.QUOTE_TIMEOUT]: 'The quote service is slow right now. Try again in a moment.',
  [ErrorClass.BRIDGE_CREATE_FAILED]:
    'Tap Retry. Your funds are still in your wallet; the bridge step will be re-attempted.',
  [ErrorClass.TON_TX_UNCERTAIN]:
    'Do not retry this step yet: the previous wallet request may already be on-chain.',
  [ErrorClass.SOLANA_TX_UNCERTAIN]:
    'Do not retry this step yet: Spotty is still checking the existing Solana signature.',
  [ErrorClass.ROUTE_REVERTED]:
    'Bridge router rejected the route at gas estimation — usually means low liquidity, insufficient balance, or a temporary issue. Try again or pick another token.',
}

export function getRecoveryHint(errorClass: ErrorClass | null): string | null {
  if (!errorClass) return null
  return RECOVERY_HINTS[errorClass] ?? null
}

export function isRecoverable(errorClass: ErrorClass | null): boolean {
  return isRecoverableError(errorClass)
}
